import React from "react";

import { FieldState, InterfaceFieldApiState } from "../../../types";

// utils
import { getErrorMessagesFromZodErrors } from "../../../utils";

// components
import { InputWrapper, InputWrapperProps } from "../../../../../molecules";
// context
import { usePyForm } from "../../../context";
import ErrorScroll from "../ErrorScroll";

export type BaseFormFieldProps<T> = InputWrapperProps & {
	input: (field: FieldState<T>) => React.ReactNode;
	validators?: {
		onChange?: ({ value, fieldApi }: { value: T; fieldApi: InterfaceFieldApiState<T> }) => void;
		onChangeListenTo?: string[];
		onBlur?: ({ value, fieldApi }: { value: T; fieldApi: InterfaceFieldApiState<T> }) => void;
		onSubmit?: ({ value, fieldApi }: { value: T; fieldApi: InterfaceFieldApiState<T> }) => void;
	};
};

/**
 * This is a Base Form Field component that wraps an input and prepares it for the context within Form
 * It supplies an input wrapper that is already hooked into the form context and has the correct props
 * You should ALWAYS try to use one of the existing Form input components, which exist for each input type by prepending Form to the input component name
 * This component is ultimately a fallback for unique inputs within forms
 *
 * @param input - The input component to wrap
 * @param validators - Optional validators for cross-field validation
 * @param props - Input Wrapper Props crosscut with some form field props
 * @returns The wrapped input component
 */
export const FormField = <T,>({ input, validators, ...props }: BaseFormFieldProps<T>) => {
	const form = usePyForm();
	return (
		<form.Field<T> name={props.id} mode="value" validators={validators}>
			{(field) => {
				// get the errors from meta and serverError and format them for display
				const errorsMeta: { message: string }[] =
					(field.state.meta.errors as unknown as { message: string }[]) || [];
				const errors = field.state?.meta?.serverError || getErrorMessagesFromZodErrors(errorsMeta);

				const value = field.state.value;
				const setValue = field.handleChange;
				const touched = field.state.meta.isTouched;
				const hasErrors = Boolean(errors && errors.length > 0);

				return (
					<ErrorScroll id={field.name} hasErrors={hasErrors}>
						<InputWrapper
							{...props}
							id={field.name}
							label={props.label}
							error={hasErrors && touched ? errors : undefined}
						>
							{input({
								value: value,
								onChange: setValue,
								onBlur: field.handleBlur,
								errors: errorsMeta,
							})}
						</InputWrapper>
					</ErrorScroll>
				);
			}}
		</form.Field>
	);
};

FormField.displayName = "FormField";

export default FormField;
